import { isCancelledError as isQueryCancelledError } from '@tanstack/react-query'

/** An error carrying the HTTP status, so callers can branch on 404 / 409 / 5xx. */
export class ApiError extends Error {
  readonly status: number
  readonly code: string | undefined
  readonly details: unknown

  constructor(message: string, status: number, code?: string, details?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = details
  }

  /** 4xx responses will not succeed on retry; 408/429 are the exceptions. */
  get isRetryable() {
    return this.status >= 500 || this.status === 408 || this.status === 429
  }
}

export function isNotFound(error: unknown) {
  return error instanceof ApiError && error.status === 404
}

/** TanStack Query cancellation or fetch `AbortSignal` — not a real failure. */
export function isCancelledError(error: unknown): boolean {
  if (isQueryCancelledError(error)) return true
  if (error instanceof DOMException && error.name === 'AbortError') return true
  // Query CancelledError uses message "CancelledError" but Error.name stays "Error".
  if (error instanceof Error && error.message === 'CancelledError') return true
  return false
}

/** The response body did not match the Zod schema we expect. */
export class ApiParseError extends Error {
  readonly issues: unknown

  constructor(message: string, issues: unknown) {
    super(message)
    this.name = 'ApiParseError'
    this.issues = issues
  }
}
